import { useCallback, useEffect, useMemo, useState } from "react";
import { getPedidoById, getPedidoHistorial } from "../../../services/usuarios_tienda/pedidos";
import { fileURL } from "../../../utils/fileURL";
import { formatearPrecio } from "../../../utils/moneda";
import OrderDetailTimeline from "./OrderDetailTimeline";
import "./OrderViewModal.css";

const formatDate = (dt) => {
  if (!dt) return "—";
  const raw = String(dt).split("T")[0];
  return raw ? raw.split("-").reverse().join("/") : "—";
};

const pesoText = (v, unidad) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  const u = String(unidad ?? "").trim();
  return u ? `${n.toFixed(2)} ${u}` : `${n.toFixed(2)}`;
};

function parseEstadoPago(raw) {
  const v = String(raw || "").trim().toUpperCase();
  const isPaid =
    v === "PAGADO" || v === "PAID" || v === "COMPLETED" || v === "COMPLETADO";

  if (isPaid) return { label: "Pagado", badgeClass: "paid" };
  if (v === "AUTORIZADO" || v === "AUTHORIZED") {
    return { label: "Autorizado", badgeClass: "processing" };
  }
  if (v === "FALLIDO" || v === "FAILED") {
    return { label: "Fallido", badgeClass: "inactive" };
  }
  if (v === "REEMBOLSADO" || v === "REFUNDED") {
    return { label: "Reembolsado", badgeClass: "inactive" };
  }
  return { label: "No pagado", badgeClass: "pending" };
}

function orderStatusBadgeClass(pedido) {
  const code = String(pedido?.codigo_estado_pedido || "").trim().toUpperCase();
  switch (code) {
    case "PENDIENTE":
      return "pending";
    case "EN_PROCESO":
      return "processing";
    case "EN_RUTA":
      return "sent";
    case "ENTREGADO":
      return "active";
    case "CANCELADO":
      return "inactive";
    default:
      break;
  }

  const n = String(pedido?.estado_texto || pedido?.estado_nombre || "").toLowerCase();
  if (n.includes("entreg")) return "active";
  if (n.includes("cancel")) return "inactive";
  if (n.includes("ruta") || n.includes("enviad")) return "sent";
  if (n.includes("proceso")) return "processing";
  return "pending";
}

function OrderViewModal({ pedidoId, onClose, configuracionTienda }) {
  const [pedido, setPedido] = useState(null);
  const [historial, setHistorial] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingHistorial, setLoadingHistorial] = useState(false);

  const fmtMoney = useCallback(
    (v) =>
      formatearPrecio(v, configuracionTienda, { espacioEntreSimbolo: true }) || "—",
    [configuracionTienda]
  );

  useEffect(() => {
    if (!pedidoId) return;

    let alive = true;

    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);

    (async () => {
      setLoading(true);
      setLoadingHistorial(true);
      setPedido(null);
      setHistorial([]);

      try {
        const [data, history] = await Promise.all([
          getPedidoById(pedidoId),
          getPedidoHistorial(pedidoId).catch(() => []),
        ]);
        if (!alive) return;
        setPedido(data || null);
        setHistorial(Array.isArray(history) ? history : []);
      } catch (e) {
        console.error("Error cargando pedido:", e);
        if (!alive) return;
        setPedido(null);
        setHistorial([]);
      } finally {
        if (alive) {
          setLoading(false);
          setLoadingHistorial(false);
        }
      }
    })();

    return () => {
      alive = false;
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [pedidoId, onClose]);

  const customerName = useMemo(() => {
    const full = `${pedido?.nombre_cliente || ""} ${pedido?.apellido_cliente || ""}`.trim();
    return full || "—";
  }, [pedido?.nombre_cliente, pedido?.apellido_cliente]);

  const phoneText = useMemo(() => {
    const code = String(pedido?.codigo_telefono || "").trim();
    const phone = String(pedido?.telefono_cliente || pedido?.telefono || "").trim();
    return `${code} ${phone}`.trim() || "—";
  }, [pedido?.codigo_telefono, pedido?.telefono_cliente, pedido?.telefono]);

  const closeOverlay = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  if (!pedidoId) return null;

  const orderStatusText = pedido?.estado_texto || pedido?.estado_nombre || "—";
  const payInfo = parseEstadoPago(pedido?.estado_pago);

  const items = Array.isArray(pedido?.items)
    ? pedido.items
    : Array.isArray(pedido?.detalles)
    ? pedido.detalles
    : [];

  const getCoreUnit = (d) => Number(d?.core_charge_unit ?? d?.core_charge ?? 0);

  const addressLines = [
    pedido?.direccion_linea_1,
    pedido?.direccion_linea_2,
    [pedido?.ciudad, pedido?.estado_provincia].filter(Boolean).join(", "),
    [pedido?.codigo_postal, pedido?.pais].filter(Boolean).join(" · "),
  ].filter(Boolean);

  return (
    <div className="ovm-overlay" onMouseDown={closeOverlay}>
      <div
        className="ovm-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Detalles del pedido"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className="ovm-header">
          <div className="ovm-header__main">
            <p className="ovm-kicker">Detalle del pedido</p>
            <h2 className="ovm-title">
              Pedido <span className="ovm-order-id">#{pedidoId}</span>
            </h2>
            <p className="ovm-meta">
              {loading
                ? "Cargando información..."
                : `Creado el ${formatDate(pedido?.fecha_creado)}`}
            </p>
          </div>

          <div className="ovm-header__actions">
            <span className={`ovm-status ${orderStatusBadgeClass(pedido)}`}>
              {loading ? "…" : orderStatusText}
            </span>
            <span className={`ovm-status ${payInfo.badgeClass}`}>
              {loading ? "…" : payInfo.label}
            </span>
            <button
              type="button"
              className="ovm-close"
              onClick={onClose}
              aria-label="Cerrar"
            >
              ×
            </button>
          </div>
        </header>

        <div className="ovm-body">
          <div className="ovm-shell">
            <div className="ovm-main">
          {loading ? (
            <div className="ovm-loading">
              <div className="ovm-skeleton ovm-skeleton--wide" />
              <div className="ovm-skeleton-grid">
                <div className="ovm-skeleton ovm-skeleton--panel" />
                <div className="ovm-skeleton ovm-skeleton--panel" />
              </div>
              <div className="ovm-skeleton ovm-skeleton--table" />
            </div>
          ) : (
            <>
              <section className="ovm-summary">
                <div className="ovm-stat">
                  <span className="ovm-stat__label">Total</span>
                  <span className="ovm-stat__value">
                    {fmtMoney(pedido?.total_factura)}
                  </span>
                </div>
                <div className="ovm-stat">
                  <span className="ovm-stat__label">Subtotal</span>
                  <span className="ovm-stat__value">{fmtMoney(pedido?.subtotal)}</span>
                </div>
                <div className="ovm-stat">
                  <span className="ovm-stat__label">Envío</span>
                  <span className="ovm-stat__value">
                    {fmtMoney(pedido?.costo_envio)}
                  </span>
                </div>
                <div className="ovm-stat">
                  <span className="ovm-stat__label">Core charge</span>
                  <span className="ovm-stat__value">
                    {fmtMoney(pedido?.core_charge_total)}
                  </span>
                </div>
              </section>

              <div className="ovm-grid">
                <section className="ovm-panel">
                  <h3 className="ovm-panel__title">Cliente</h3>
                  <dl className="ovm-kv">
                    <div className="ovm-kv__row">
                      <dt>Nombre</dt>
                      <dd>{customerName}</dd>
                    </div>
                    <div className="ovm-kv__row">
                      <dt>Correo</dt>
                      <dd>{pedido?.correo || "—"}</dd>
                    </div>
                    <div className="ovm-kv__row">
                      <dt>Teléfono</dt>
                      <dd>{phoneText}</dd>
                    </div>
                  </dl>
                </section>

                <section className="ovm-panel">
                  <h3 className="ovm-panel__title">Envío y entrega</h3>
                  <dl className="ovm-kv">
                    <div className="ovm-kv__row">
                      <dt>Método</dt>
                      <dd>{pedido?.nombre_metodo_envio || "—"}</dd>
                    </div>
                    <div className="ovm-kv__row">
                      <dt>Transportista</dt>
                      <dd>{pedido?.metodo_envio_compania || "—"}</dd>
                    </div>
                    <div className="ovm-kv__row">
                      <dt>Cuenta / Ref</dt>
                      <dd>{pedido?.metodo_envio_no_cuenta || "—"}</dd>
                    </div>
                    <div className="ovm-kv__row">
                      <dt>Destinatario</dt>
                      <dd>{pedido?.nombre_destinatario || "—"}</dd>
                    </div>
                    <div className="ovm-kv__row ovm-kv__row--stack">
                      <dt>Dirección</dt>
                      <dd>
                        {addressLines.length ? (
                          addressLines.map((line, idx) => (
                            <span key={idx} className="ovm-address-line">
                              {line}
                            </span>
                          ))
                        ) : (
                          "—"
                        )}
                      </dd>
                    </div>
                  </dl>
                </section>
              </div>

              <section className="ovm-panel ovm-panel--products">
                <div className="ovm-panel__head">
                  <h3 className="ovm-panel__title">Productos</h3>
                  <span className="ovm-panel__count">
                    {items.length} producto{items.length === 1 ? "" : "s"}
                  </span>
                </div>

                <div className="ovm-table-wrap">
                  <table className="ovm-table ovm-table--cards">
                    <thead>
                      <tr>
                        <th>Producto</th>
                        <th className="ovm-td-right">Unitario</th>
                        <th className="ovm-td-right">Core</th>
                        <th className="ovm-td-right">Cant.</th>
                        <th className="ovm-td-right">Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="ovm-empty">
                            No se encontraron productos.
                          </td>
                        </tr>
                      ) : (
                        items.map((d, idx) => {
                          const rawImg = d?.url_image;
                          const imgSrc = rawImg ? fileURL(rawImg) : null;
                          const coreUnit = getCoreUnit(d);
                          const precioUnit = Number(d?.precio_unitario || 0);
                          const precioOriginal =
                            d?.precio_original != null ? Number(d.precio_original) : null;
                          const enOferta =
                            precioOriginal != null && precioOriginal > precioUnit;
                          const descuentoPct = enOferta
                            ? Math.round(
                                ((precioOriginal - precioUnit) / precioOriginal) * 100
                              )
                            : 0;
                          const key =
                            d?.pedido_detalles_id ??
                            `${d?.producto_id ?? "p"}-${idx}`;

                          return (
                            <tr key={key} className="ovm-row">
                              <td className="ovm-prod-cell" data-label="Producto">
                                <div className="ovm-prod">
                                  <div className="ovm-thumb">
                                    {imgSrc ? (
                                      <img
                                        src={imgSrc}
                                        alt={d.nombre_producto || "Producto"}
                                        loading="lazy"
                                        onError={(e) => {
                                          e.currentTarget.style.display = "none";
                                          const fb =
                                            e.currentTarget.parentElement?.querySelector(
                                              ".ovm-thumb__fallback"
                                            );
                                          if (fb) fb.style.display = "flex";
                                        }}
                                      />
                                    ) : null}
                                    <div
                                      className="ovm-thumb__fallback"
                                      style={{ display: imgSrc ? "none" : "flex" }}
                                    >
                                      —
                                    </div>
                                  </div>
                                  <div className="ovm-prod__info">
                                    <span className="ovm-prod__name">
                                      {d.nombre_producto || "—"}
                                    </span>
                                    <span className="ovm-prod__meta">
                                      SKU {d.no_parte || "—"}
                                      {d.peso_producto
                                        ? ` · ${pesoText(d.peso_producto, d.unidad_peso)}`
                                        : ""}
                                    </span>
                                    {enOferta && (
                                      <span className="ovm-prod__offer-tag">
                                        Oferta −{descuentoPct}%
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td className="ovm-td-right" data-label="Unitario">
                                {enOferta ? (
                                  <span className="ovm-price-offer">
                                    <span className="ovm-price-old">
                                      {fmtMoney(precioOriginal)}
                                    </span>
                                    <span className="ovm-price-new">
                                      {fmtMoney(precioUnit)}
                                    </span>
                                  </span>
                                ) : (
                                  fmtMoney(d.precio_unitario)
                                )}
                              </td>
                              <td className="ovm-td-right" data-label="Core">
                                {fmtMoney(coreUnit)}
                              </td>
                              <td className="ovm-td-right" data-label="Cant.">
                                {d.cantidad ?? "—"}
                              </td>
                              <td className="ovm-td-right ovm-td-strong" data-label="Subtotal">
                                {fmtMoney(d.subtotal ?? d.total)}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </section>

            </>
          )}
            </div>

            <OrderDetailTimeline
              historial={historial}
              pedido={pedido}
              loading={loading || loadingHistorial}
            />
          </div>
        </div>

        <footer className="ovm-footer">
          <button type="button" className="ovm-btn" onClick={onClose}>
            Cerrar
          </button>
        </footer>
      </div>
    </div>
  );
}

export default OrderViewModal;
